/**
 * VE-29142 — the editor's left-panel layout, remembered across sessions.
 *
 * THREE SCOPES, three storage keys, and the scope is the point of each:
 *
 *  - PER REQUEST (`readPanelPreferences`): which panels are open, and whether
 *    the Documents panel is pinned. Which panels a reviewer wants is a property
 *    of the work — a request of forty documents wants the Documents panel
 *    pinned, a single-file request wants the canvas — so one global preference
 *    would make every request fight the last one. Keyed by folder id, bucketed
 *    per org.
 *  - PER WINDOW BAND (`readToolPanelExpanded`): whether the tool rail shows
 *    its buttons' names. A matter of screen room rather than of the work, so
 *    it is keyed by the band the window is in; a band never chosen in uses
 *    that band's default.
 *  - PER BROWSER (`readLastWindowWidth`): the window width the editor last
 *    opened at, which decides whether a stored layout still FITS. Not bucketed
 *    per org at all: a window's size belongs to the browser, and an org switch
 *    forgetting how big the screen is would re-apply defaults for no reason the
 *    reviewer could see.
 *
 * The bands those widths fall into, and the defaults each implies, live here
 * too — see `layoutTierFor` and `layoutDefaultsFor`.
 *
 * `localStorage` rather than redux alone, because redux resets on reload and a
 * pin that forgets overnight reads as a bug rather than a preference. Mirrors
 * `readStoredFileViewMode` in casemedia, including the try/catch: localStorage
 * throws in privacy modes and is absent entirely when a module is pulled into
 * the web-worker bundle.
 *
 * Deliberately NOT stored: the document order. That is session-only by
 * decision (see `DocumentsStore.documentOrder`) and needs a real home before it
 * persists — it will be read by the combined export, not just this editor.
 */

// Bucketed per ORG, matching `lastOpenFolderStorageKey` in casemedia, which
// stores the same class of data (folder ids) and documents why: switching orgs
// reads a different bucket and finds nothing to restore.
//
// Precisely what that buys, since it is easy to overstate: it separates ORGS,
// not USERS. Two reviewers sharing a browser profile in the same org share a
// bucket, so one can see which request folders the other opened. The contents
// are three booleans and folder ids — no names, no document content — so the
// exposure is small, but it is not the shared-workstation protection it
// resembles. Clearing the key on LOGOUT would make it that.
//
// Callers pass the current org id; without one (before onboarding loads) reads
// and writes are no-ops.
const STORAGE_KEY_PREFIX = 'vtn-redact-document-editor-panels';

const storageKey = (orgId: string): string => `${STORAGE_KEY_PREFIX}:${orgId}`;

/** How many requests' preferences to keep. Old entries fall off the end. */
const MAX_REQUESTS = 50;

export interface PanelPreferences {
  documentsPanelOpen: boolean;
  documentsPanelPinned: boolean;
  pagesPanelCollapsed: boolean;
}

export const DEFAULT_PANEL_PREFERENCES: PanelPreferences = {
  documentsPanelOpen: false,
  documentsPanelPinned: false,
  pagesPanelCollapsed: false,
};

type StoredPreferences = Record<string, Partial<PanelPreferences>>;

const readAll = (orgId: string): StoredPreferences => {
  try {
    const raw = localStorage.getItem(storageKey(orgId));
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    // Anything else in this key is someone else's data or a corrupted write;
    // treat it as absent rather than letting it reach the reducer.
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as StoredPreferences)
      : {};
  } catch {
    return {};
  }
};

/**
 * The stored preferences for one request, merged over the defaults.
 *
 * Each field is checked individually rather than trusting the shape: this JSON
 * survives across deploys, so a key written by an older build can be missing a
 * field a newer one expects, and a half-applied preference is worse than none.
 */
export const readPanelPreferences = (
  orgId: string | undefined,
  folderId: string | undefined
): PanelPreferences => {
  if (!orgId || !folderId) {
    return DEFAULT_PANEL_PREFERENCES;
  }
  const stored = readAll(orgId)[folderId];
  if (!stored) {
    return DEFAULT_PANEL_PREFERENCES;
  }
  return {
    documentsPanelOpen:
      typeof stored.documentsPanelOpen === 'boolean'
        ? stored.documentsPanelOpen
        : DEFAULT_PANEL_PREFERENCES.documentsPanelOpen,
    documentsPanelPinned:
      typeof stored.documentsPanelPinned === 'boolean'
        ? stored.documentsPanelPinned
        : DEFAULT_PANEL_PREFERENCES.documentsPanelPinned,
    pagesPanelCollapsed:
      typeof stored.pagesPanelCollapsed === 'boolean'
        ? stored.pagesPanelCollapsed
        : DEFAULT_PANEL_PREFERENCES.pagesPanelCollapsed,
  };
};

// ---------------------------------------------------------------------------
// Default layout, chosen by window width
// ---------------------------------------------------------------------------

/**
 * The editor has three left-hand things competing for horizontal room: the tool
 * rail, the page-thumbnail rail and the canvas. Which of them a reviewer can
 * afford is a property of their WINDOW, not of their request, so the defaults
 * are picked from its width rather than shipped as one guess.
 *
 * Three bands, and the narrowest is not simply "less of everything":
 *
 *   > 1600  Both rails, and the tool rail labelled. Room for all of it.
 *   1300–1600  Both rails, tool rail as an icon strip. The thumbnails are worth
 *     more than the tool NAMES — the icons are still there, the tooltips still
 *     explain them, and the rail costs 44px instead of 156.
 *   < 1300  Thumbnails hidden, DOCUMENTS panel closed, and the tool rail
 *     still an icon strip. At this width the two rails plus a readable page do
 *     not fit, so one has to go; the thumbnails are the one with another way
 *     in (the Pages button reopens them).
 *
 *     Each band down drops ONE thing — names at medium, then thumbnails too at
 *     narrow — so the tool names are a wide-screen default only. The narrow
 *     band used to spell them out again with the space the thumbnails freed,
 *     which made medium the one band without them for no reason a reviewer
 *     could see. A reviewer who wants them turns them on once per band.
 *
 *     This is the only band that touches the Documents panel, which is
 *     otherwise carried across a tier change untouched (see the saga). Here
 *     there is no room to carry it into: it is the widest of the three panels,
 *     and leaving it open on a 1200px window would leave a page too narrow to
 *     read at all.
 *
 * Both numbers are starting points, set to be moved once someone has sat in
 * front of each.
 */
export const LAYOUT_WIDE_MIN_PX = 1600;
export const LAYOUT_NARROW_MAX_PX = 1300;

export type LayoutTier = 'wide' | 'medium' | 'narrow';

export const layoutTierFor = (width: number): LayoutTier => {
  if (width > LAYOUT_WIDE_MIN_PX) {
    return 'wide';
  }
  return width < LAYOUT_NARROW_MAX_PX ? 'narrow' : 'medium';
};

export interface LayoutDefaults {
  pagesPanelCollapsed: boolean;
  toolPanelExpanded: boolean;
  /**
   * Only set where the band has an opinion. `undefined` means "leave the
   * reviewer's Documents panel as they had it", which is every band but the
   * narrowest — see the note above.
   */
  documentsPanelOpen?: boolean;
}

export const layoutDefaultsFor = (tier: LayoutTier): LayoutDefaults => {
  switch (tier) {
    case 'wide':
      return { pagesPanelCollapsed: false, toolPanelExpanded: true };
    case 'medium':
      return { pagesPanelCollapsed: false, toolPanelExpanded: false };
    case 'narrow':
      return {
        pagesPanelCollapsed: true,
        toolPanelExpanded: false,
        documentsPanelOpen: false,
      };
  }
};

/**
 * The window width the editor last opened at.
 *
 * NOT bucketed per org, unlike everything else in this file: a browser window's
 * size belongs to the browser. Bucketing it would mean an org switch quietly
 * forgot how big the screen is and re-applied defaults for no reason the
 * reviewer could see.
 */
const WINDOW_WIDTH_KEY = 'vtn-redact-document-editor-window-width';

export const readLastWindowWidth = (): number | null => {
  try {
    const raw = localStorage.getItem(WINDOW_WIDTH_KEY);
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    // `Number('')` is 0 and `Number('x')` is NaN; neither is a width, and
    // either would land in the 'narrow' band and rearrange the editor.
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

export const persistLastWindowWidth = (width: number): void => {
  try {
    localStorage.setItem(WINDOW_WIDTH_KEY, String(width));
  } catch {
    // Best-effort; the next open simply re-applies the defaults.
  }
};

// ---------------------------------------------------------------------------
// Tool panel width — remembered PER DOCUMENT
// ---------------------------------------------------------------------------

/**
 * Whether the tool rail shows its buttons' names — one choice per window
 * BAND (see `layoutTierFor`), not per document.
 *
 * Showing the names is a question of screen room, which is exactly what the
 * bands measure: a reviewer who hides them on a laptop and shows them on a wide
 * monitor wants each screen to remember its own answer. It was per document
 * first, with the band's default applied whenever the window changed band —
 * and that default lasted one visit, after which the old choice from the OTHER
 * screen came back. Per band, a band change needs no special case at all.
 *
 * Unchosen bands use `layoutDefaultsFor`. Bucketed per org like the rest.
 */
const TOOL_PANEL_KEY_PREFIX = 'vtn-redact-document-editor-tool-panel-by-band';

const toolPanelStorageKey = (orgId: string): string =>
  `${TOOL_PANEL_KEY_PREFIX}:${orgId}`;

const readAllToolPanel = (
  orgId: string
): Partial<Record<LayoutTier, boolean>> => {
  try {
    const raw = localStorage.getItem(toolPanelStorageKey(orgId));
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Partial<Record<LayoutTier, boolean>>)
      : {};
  } catch {
    return {};
  }
};

/**
 * The reviewer's choice for this band, or `undefined` if they have not made
 * one here yet — the caller then applies the band's default.
 */
export const readToolPanelExpanded = (
  orgId: string | undefined,
  tier: LayoutTier
): boolean | undefined => {
  if (!orgId) {
    return undefined;
  }
  const stored = readAllToolPanel(orgId)[tier];
  // Checked rather than coerced, for the reason the request preferences are:
  // this JSON outlives deploys, and anything that is not a boolean is a write
  // from a build that meant something else by this key.
  return typeof stored === 'boolean' ? stored : undefined;
};

export const persistToolPanelExpanded = (
  orgId: string | undefined,
  tier: LayoutTier,
  expanded: boolean
): void => {
  if (!orgId) {
    return;
  }
  try {
    localStorage.setItem(
      toolPanelStorageKey(orgId),
      JSON.stringify({ ...readAllToolPanel(orgId), [tier]: expanded })
    );
  } catch {
    // Best-effort — the choice simply won't survive the reload.
  }
};

export const persistPanelPreferences = (
  orgId: string | undefined,
  folderId: string | undefined,
  preferences: PanelPreferences
): void => {
  if (!orgId || !folderId) {
    return;
  }
  try {
    const all = readAll(orgId);
    // Re-insert so this request becomes the most recent key. Object key order
    // is insertion order for string keys, which is what the trim below relies
    // on — it is a bounded cache, not a database.
    delete all[folderId];
    const next: StoredPreferences = { ...all, [folderId]: preferences };
    const keys = Object.keys(next);
    const trimmed =
      keys.length > MAX_REQUESTS
        ? Object.fromEntries(
            keys.slice(keys.length - MAX_REQUESTS).map((k) => [k, next[k]])
          )
        : next;
    localStorage.setItem(storageKey(orgId), JSON.stringify(trimmed));
  } catch {
    // Best-effort — the layout simply won't survive the reload.
  }
};
